import {
	getBuyersFilterApi,
	getCatchAreaApi,
	getFishingGearApi,
	getSpecies,
} from "api/common";
import { Vessel } from "interface/vessel";
import { FC, useRef, useState } from "react";
import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { DATE_FORMAT, ICON_NAMES } from "utils/constant";
import { getSalesNoteStatus, SalesNoteFilter } from "api/salesNote";
import { useTranslation } from "react-i18next";
import { VesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";
import { getVesselFilter } from "api/vessel";
import DatePickerComponent from "ui/Date";
import { CustomNumberInput } from "ui/Input";
import { startOfDay } from "date-fns";
import { FishingGear, Specie } from "interface/common";
import { Buyer } from "interface/buyer";

interface CustomProps {
	pageSize: number;
	setFilterOptions: (options: SalesNoteFilter) => void;
	filterOptions: SalesNoteFilter;
	exportAsPDF: () => void;
	exportAsExcel: () => void;
}
const Filter: FC<CustomProps> = ({
	setFilterOptions,
	filterOptions,
	/*exportAsPDF,*/
	...props
}) => {
	const { t } = useTranslation();

	const [localFromDate, setLocalFromDate] = useState(filterOptions.fromDate);
	const [localToDate, setLocalToDate] = useState(filterOptions.toDate);
	const pendingFromDate = useRef(filterOptions.fromDate);
	const pendingToDate = useRef(filterOptions.toDate);
	const [tempFilter, setTempFilter] = useState<SalesNoteFilter>();

	const isSameDate = (
		a: Date | null | undefined,
		b: Date | null | undefined,
	) => {
		if (!a || !b) return a === b;
		return startOfDay(a).getTime() === startOfDay(b).getTime();
	};

	const applyNoteNumber = () => {
		const newValue = tempFilter?.noteNumber || null;
		const currentValue = filterOptions.noteNumber || null;
		if (newValue === currentValue) return;
		setFilterOptions({
			...filterOptions,
			noteNumber: newValue,
			take: props.pageSize,
			skip: 0,
		});
	};

	const applyFromDate = (value = pendingFromDate.current) => {
		if (isSameDate(value, filterOptions.fromDate)) return;
		setFilterOptions({
			...filterOptions,
			fromDate: value,
			take: props.pageSize,
			skip: 0,
		});
	};

	const applyToDate = (value = pendingToDate.current) => {
		if (isSameDate(value, filterOptions.toDate)) return;
		setFilterOptions({
			...filterOptions,
			toDate: value,
			take: props.pageSize,
			skip: 0,
		});
	};

	const handleVessel = (vessel: Vessel) => {
		setFilterOptions({
			...filterOptions,
			vesselHullId: vessel.id as any,
			vesselId: vessel.id,
			vesselName: vessel.id ? vessel.nm : "",
			vesselRegistrationNumber: vessel.id ? vessel.rn : "",
			take: props.pageSize,
			skip: 0,
		});
	};

	const handleSpecies = (species: Specie) => {
		setFilterOptions({
			...filterOptions,
			speciesCode: species.code || null,
			speciesName: species.name || "",
			take: props.pageSize,
			skip: 0,
		});
	};

	const getSpeciesFilterApi = (text: string) => {
		return getSpecies(text);
	};

	return (
		<div className="filter-container">
			<div className="filter-items sales-note-filter-items">
				<div style={{ width: "170px" }}>
					<DatePickerComponent
						placeholder={t("FromDate")}
						label={t("FromDate")}
						format={DATE_FORMAT}
						className="height-inherit"
						style={{ width: "100%" }}
						value={localFromDate}
						onChange={(e) => {
							pendingFromDate.current = e.value;
							setLocalFromDate(e.value);
							if (e.syntheticEvent?.type === "click") {
								applyFromDate(e.value);
							}
						}}
						onBlur={() => applyFromDate()}
					/>
				</div>
				<div style={{ width: "170px" }}>
					<DatePickerComponent
						placeholder={t("ToDate")}
						label={t("ToDate")}
						className="height-inherit"
						format={DATE_FORMAT}
						style={{ width: "100%" }}
						value={localToDate}
						onChange={(e) => {
							pendingToDate.current = e.value;
							setLocalToDate(e.value);
							if (e.syntheticEvent?.type === "click") {
								applyToDate(e.value);
							}
						}}
						onBlur={() => applyToDate()}
					/>
				</div>
				<div style={{ width: "170px" }}>
					<CustomDropDownList
						data={[]}
						filterable={true}
						dataItemKey="id"
						textField="nm"
						label={t("Vessel")}
						value={{
							id: filterOptions.vesselId || null,
							nm: filterOptions.vesselName || " ",
						}}
						defaultItem={{ id: null, nm: t("All") }}
						onSelect={handleVessel}
						serverFilter={true}
						itemRender={VesselRenderItem()}
						serverFilterApi={getVesselFilter}
					/>
				</div>
				<div style={{ width: "170px" }}>
					<CustomDropDownList
						onSelect={(buyer: Buyer) =>
							setFilterOptions({
								...filterOptions,
								buyerId: buyer.id as any,
								buyerName: buyer.name,
								take: props.pageSize,
								skip: 0,
							})
						}
						data={[]}
						value={{
							id: filterOptions.buyerId || null,
							displayName: filterOptions.buyerName || ' '
						}}
						dataItemKey="id"
						textField="displayName"
						label={t("Buyer")}
						defaultItem={{ id: null, displayName: t("All") }}
						className="height-inherit"
						filterable={true}
						serverFilter={true}
						serverFilterApi={getBuyersFilterApi}
					/>
				</div>
				<div style={{ width: "170px" }}>
					<CustomDropDownList
						data={[]}
						label={t("FishingGear")}
						dataItemKey="code"
						textField="displayName"
						value={{
							code: filterOptions.fishingGearCode || null,
							displayName: filterOptions.fishingGear || " ",
						}}
						defaultItem={{ code: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getFishingGearApi}
						onSelect={(gear: FishingGear) =>
							setFilterOptions({
								...filterOptions,
								fishingGearCode: gear.code ?? undefined,
								fishingGear: gear.name,
								take: props.pageSize,
								skip: 0,
							})
						}
						className="height-inherit"
						popupSettings={{ width: 400 }}
					/>
				</div>
				<div style={{ width: "170px" }}>
					<CustomDropDownList
						data={[]}
						label={t("CatchArea")}
						dataItemKey="code"
						textField="displayName"
						value={{
							code: filterOptions.catchFieldCode || null,
							displayName: filterOptions.catchFieldName || " ",
						}}
						defaultItem={{ zone: null, displayName: t("All") }}
						filterable={true}
						serverFilter={true}
						serverFilterApi={getCatchAreaApi}
						onSelect={(item) => {
							setFilterOptions({
								...filterOptions,
								catchFieldCode: item.code ?? undefined,
								catchFieldName: item.displayName,
								take: props.pageSize,
								skip: 0,
							});
						}}
						className="height-inherit"
						popupSettings={{ width: 400 }}
					/>
				</div>
			</div>

			<div className="filter-end-btn sales-note-filter-end-btn">
				<CustomButton
					label={"PDF"}
					onClick={() => props.exportAsPDF()}
					iconName={ICON_NAMES.Download}
				/>
				<CustomButton
					label={"Excel"}
					onClick={() => props.exportAsExcel()}
					iconName={ICON_NAMES.Download}
				/>
			</div>
		</div>
	);
};

export default Filter;
